// libraries
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Dinero from 'dinero.js'

// queries and mutations
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// helpers and utils
import { setDecimalPrecision } from 'utils/helpers'
import { getNetValue } from 'utils/data-manipulation/common'
import { keyBy } from 'lodash'

const getPriceEntryPrice = priceEntry =>
  priceEntry && typeof priceEntry?.buyPrice?.price === 'object'
    ? Dinero({ ...priceEntry?.buyPrice?.price }).toUnit()
    : Number(priceEntry?.buyPrice?.price) || 0

const useUpdateIndustrialIBTPrices = ({ supplementalPriceSheetIds, materialsApiRef, isDirty }) => {
  const { defaultYardUm } = useSelector(state => state.settings)

  const materialTypeIds = useMemo(() => {
    if (!materialsApiRef?.current?.getRowModels) return []
    return [
      ...new Set(
        Array.from(materialsApiRef.current.getRowModels().values())
          .map(material => material.materialTypeId)
          .filter(Boolean),
      ),
    ]
  }, [materialsApiRef, supplementalPriceSheetIds])

  const { data: materialTypesData, isFetching } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ materialTypeIds, supplementalPriceSheetIds }) },
    { skip: materialTypeIds.length === 0 },
  )

  const materialTypesDict = useMemo(() => keyBy(materialTypesData?.Items, 'id') || {}, [materialTypesData])

  useEffect(() => {
    if (!isDirty || !materialTypesData) return

    const materials = Array.from(materialsApiRef.current.getRowModels().values())
    const updatedRows = materials.map(material => {
      const { gross, tare, deductions, id, materialTypeId } = material
      const { supplementalPriceEntry, defaultPriceEntry } = materialTypesDict[materialTypeId] || {}
      const priceEntry = supplementalPriceEntry || defaultPriceEntry

      const newPrice = getPriceEntryPrice(priceEntry)
      const newUm = priceEntry?.weightUnits || material?.um || defaultYardUm

      const netValue = getNetValue({
        gross: Number(gross) || 0,
        tare: Number(tare) || 0,
        price: newPrice,
        deductions,
        fromUnit: defaultYardUm,
        toUnit: newUm,
      })

      return {
        id,
        um: newUm,
        customerPreferredUm: newUm,
        price: setDecimalPrecision(newPrice, { decimalPrecisionOverride: 4, shouldFormatNumber: false }),
        customerPreferredPrice: setDecimalPrecision(newPrice, {
          decimalPrecisionOverride: 4,
          shouldFormatNumber: false,
        }),
        netValue,
        defaultPriceEntry,
        overridePriceEntry: supplementalPriceEntry,
      }
    })

    materialsApiRef.current.updateRows(updatedRows)
  }, [materialTypesDict])

  return { isFetching, materialTypesData }
}

export default useUpdateIndustrialIBTPrices
